import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import api from "../../services/api";

interface Grade {
  id: number;
  code: string;
  name: string;
  description: string;
  level: number;
  is_active: boolean;
}

interface Category {
  id: number;
  name: string;
}

interface Designation {
  id: number;
  name: string;
}

const GradeManagement: React.FC = () => {
  const [grades, setGrades] = useState<Grade[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null);
  const [mappingType, setMappingType] = useState<"category" | "designation">(
    "category",
  );
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
    level: 0,
  });

  useEffect(() => {
    fetchGrades();
    fetchCategories();
    fetchDesignations();
  }, []);

  const fetchGrades = async () => {
    try {
      const response = await api.get("/grades");
      setGrades(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching grades:", error);
      setError("Failed to load grades");
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("/admin/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchDesignations = async () => {
    try {
      const response = await api.get("/designations");
      setDesignations(response.data);
    } catch (error) {
      console.error("Error fetching designations:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedGrade) {
        await api.put(`/grades/${selectedGrade.id}`, formData);
        setSuccess("Grade updated successfully");
      } else {
        await api.post("/grades", formData);
        setSuccess("Grade created successfully");
      }
      setShowAddModal(false);
      setFormData({ code: "", name: "", description: "", level: 0 });
      setSelectedGrade(null);
      fetchGrades();
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to save grade");
    }
  };

  const handleEdit = (grade: Grade) => {
    setSelectedGrade(grade);
    setFormData({
      code: grade.code,
      name: grade.name,
      description: grade.description || "",
      level: grade.level,
    });
    setShowAddModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to deactivate this grade?"))
      return;
    try {
      await api.delete(`/grades/${id}`);
      setSuccess("Grade deleted successfully");
      fetchGrades();
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to delete grade");
    }
  };

  const handleAddMapping = async (itemId: number) => {
    try {
      const endpoint =
        mappingType === "category" ? "categories" : "designations";
      const payload =
        mappingType === "category"
          ? { categoryId: itemId }
          : { designationId: itemId };

      await api.post(`/grades/${selectedGrade?.id}/${endpoint}`, payload);
      setSuccess("Mapping added successfully");
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to add mapping");
    }
  };

  const openMappingModal = (grade: Grade, type: "category" | "designation") => {
    setSelectedGrade(grade);
    setMappingType(type);
    setShowMappingModal(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Grade Management</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => {
            setSelectedGrade(null);
            setFormData({ code: "", name: "", description: "", level: 0 });
            setShowAddModal(true);
          }}
        >
          Add New Grade
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grades.map((grade) => (
              <TableRow key={grade.id} hover>
                <TableCell>{grade.code}</TableCell>
                <TableCell>{grade.name}</TableCell>
                <TableCell>{grade.level}</TableCell>
                <TableCell>{grade.description}</TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleEdit(grade)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Map Categories">
                    <IconButton
                      onClick={() => openMappingModal(grade, "category")}
                      size="small"
                      color="success"
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Map Designations">
                    <IconButton
                      onClick={() => openMappingModal(grade, "designation")}
                      size="small"
                      color="secondary"
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => handleDelete(grade.id)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Modal */}
      <Dialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedGrade ? "Edit Grade" : "Add New Grade"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Code"
              value={formData.code}
              onChange={(e) =>
                setFormData({ ...formData, code: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Level (Higher = Senior)"
              type="number"
              value={formData.level}
              onChange={(e) =>
                setFormData({ ...formData, level: parseInt(e.target.value) })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddModal(false);
              setSelectedGrade(null);
              setFormData({ code: "", name: "", description: "", level: 0 });
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedGrade ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mapping Modal */}
      <Dialog
        open={showMappingModal}
        onClose={() => setShowMappingModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Map {mappingType === "category" ? "Categories" : "Designations"} to{" "}
          {selectedGrade?.name}
        </DialogTitle>
        <DialogContent>
          <List sx={{ maxHeight: 400, overflow: "auto" }}>
            {mappingType === "category"
              ? categories.map((category) => (
                  <ListItem key={category.id}>
                    <ListItemText primary={category.name} />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAddMapping(category.id)}
                      >
                        Add
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
              : designations.map((designation) => (
                  <ListItem key={designation.id}>
                    <ListItemText primary={designation.name} />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAddMapping(designation.id)}
                      >
                        Add
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMappingModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GradeManagement;
